import {
  Typography,
  Autocomplete,
  TextField,
  IconButton,
  TablePagination,
  Divider,
  TableCell,
  TableHead,
  TableBody,
  Table,
  TableRow,
  MenuItem,
  Menu,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  Snackbar,
  Alert,
  AlertTitle,
  Fab,
  TableContainer,
  Input,
  Skeleton,
  CircularProgress,
} from '@mui/material'
import '../../lines/LinesShared.css'
import './DraftLines.css'
import ActionBar from '../../../Misc/ActionBar'
import { BUNDLE_NON_EDIT_PROPERTIES, SnackbarState } from '../../lines/Lines'
import { useQuoteLines } from '../../lines/QuoteLinesContext'
import { quoteStatuses } from 'src/lists'
import { useBeforeUnload, useBlocker, useParams } from 'react-router-dom'
import QuoteMenu from 'src/components/shared/QuoteMenu'
import {
  AddBoxOutlined,
  DifferenceOutlined,
  DeleteOutlined,
  EditOutlined,
  MoreVert,
  ArrowDropDown,
  ExpandMore,
  ExpandLess,
  ChevronRight,
  ChevronLeft,
  DragIndicator,
} from '@mui/icons-material'
import { useCallback, useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react'
import { quoteLineType } from '../../../types'
import { LineEdit } from '../../lines/LineEdit'
import { AiClassifyingLabel } from '../../lines/AiClassifyingLabel'
import { useProductCategoryOptions } from '../../lines/productCategories'
import axios from 'axios'
import { useCustomViewTemplates, useUser } from '../../../hooks'
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd'
import { DeleteDialog } from '../../../components/shared/DeleteDialog'
import {
  CloneDialog,
  DeleteDialog as DeleteQuoteDialog,
  NewVersionDialog,
  NTECalculationDialog,
} from '../../../QuoteDetails/QuoteDetailsOptionsDialogs'
import { COLUMN_MAP, DEFAULT_TEMPLATE, getColumnValue, getColumnWidth } from '../../lines/LinesColumns'
import { EditableCell } from './EditableCell'
import HeaderCRMDetails from '../../lines/HeaderCRMDetails'
import SupplierQuotesSidebar from '../../lines/SupplierQuotesSidebar'
import { BundleLineItem, updateLineItemProperty } from 'src/EditQuote/lines/lineItemPricing'
import QuoteSummary from './QuoteSummary'

type AdjustmentType = 'add' | 'differences' | 'delete'

const ADJUSTMENT_LABELS: Record<AdjustmentType, string> = {
  add: 'Additions',
  differences: 'Differences',
  delete: 'Deletions',
}

function countActualLineItems(items: BundleLineItem[]) {
  return (
    items.flatMap(bundle => bundle.children).filter(item => item?.LineNumber !== -1).length +
    items.filter(item => item.LineNumber !== -1).length
  )
}

function filterLineItemsByVendorQuote(items: BundleLineItem[], vendorQuoteIds: string[]) {
  return items
    .map(item =>
      item.LineNumber === -1
        ? {
            ...item,
            children: item.children?.filter(
              child => child.VendorQuoteId && vendorQuoteIds.includes(child.VendorQuoteId),
            ),
          }
        : item,
    )
    .filter(item =>
      item.LineNumber === -1
        ? (item.children?.length ?? 0) > 0
        : !!item.VendorQuoteId && vendorQuoteIds.includes(item.VendorQuoteId),
    )
}

function paginateLineItems(items: BundleLineItem[], page: number, rowsPerPage: number) {
  type FlatItem = {
    item: quoteLineType
    bundleHeader?: BundleLineItem
    bundleId: string | null
  }
  const flatItems: FlatItem[] = items.flatMap(bundle =>
    bundle.LineNumber === -1
      ? (bundle.children ?? []).map<FlatItem>(child => ({
          item: child,
          bundleHeader: bundle,
          bundleId: bundle.Bundle || null,
        }))
      : ([{ item: bundle, bundleId: null }] as FlatItem[]),
  )

  const pageItems = flatItems.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  const bundleGroups = new Map<string, { header: BundleLineItem; items: quoteLineType[] }>()
  const result: BundleLineItem[] = []
  const seenBundles = new Set<string>()

  for (const { item, bundleHeader, bundleId } of pageItems) {
    if (bundleId && bundleHeader) {
      if (!bundleGroups.has(bundleId)) {
        bundleGroups.set(bundleId, { header: bundleHeader, items: [] })
      }
      bundleGroups.get(bundleId)?.items.push(item)
    }
  }

  for (const { item, bundleHeader, bundleId } of pageItems) {
    if (bundleId && bundleHeader) {
      if (!seenBundles.has(bundleId)) {
        seenBundles.add(bundleId)
        result.push({ ...bundleHeader, children: bundleGroups.get(bundleId)?.items ?? [] })
      }
    } else {
      result.push({ ...item, children: [] })
    }
  }

  return result
}

function DraftLines({
  validateLoading,
  setValidateLoading,
  setView,
}: {
  validateLoading: boolean
  setValidateLoading: Dispatch<SetStateAction<boolean>>
  setView: Dispatch<SetStateAction<number>>
}) {
  const {
    lineItems,
    setLineItems,
    lineItemsLoading,
    sortedLineItems,
    selectedLineItems,
    setSelectedLineItems,
    setBulkEditSnackbar,
    setBundleSnackbar,
    lineCount,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    canEdit,
    collapsedBundles,
    setCollapsedBundles,
    drawerState,
    setDrawerState,
    cloneSnackbar,
    setCloneSnackbar,
    deleteSnackbar,
    setDeleteSnackbar,
    bulkEditSnackbar,
    bulkDeleteSnackbar,
    setBulkDeleteSnackbar,
    addLineSnackbar,
    setAddLineSnackbar,
    bundleSnackbar,
    setUnbundleSnackbar,
    unbundleSnackbar,
    handleMenuAction,
    handleDrag,
    handleEditModeToggle,
    handleResetLines,
    hasUnsavedLineChanges,
    versions,
    differences,
    handleDelete,
    handleDeleteVendorQuote,
    handleReloadLineItems,
    vendorQuotes,
    quote,
  } = useQuoteLines()
  const productCategoryOptions = useProductCategoryOptions()

  const user = useUser()
  const {
    templates: savedTemplates,
    loading: templatesLoading,
    reload: reloadTemplates,
    selectedTemplateName,
    setSelectedTemplateName,
  } = useCustomViewTemplates()
  const templates = [...savedTemplates, DEFAULT_TEMPLATE]
  const selectedTemplate =
    templates.find(template => template.TemplateName === selectedTemplateName) ?? DEFAULT_TEMPLATE
  const { quoteId } = useParams()
  const [loading, setLoading] = useState(false)
  const [columns, setColumns] = useState<{ Name: string; Width: number; Order: number }[]>([])
  const [openDialog, setOpenDialog] = useState<string | null>(null)
  const [viewName, setViewName] = useState<string>('')
  const [openViewDrawer, setOpenViewDrawer] = useState(false)
  const [saveTemplateSnackbar, setSaveTemplateSnackbar] = useState<SnackbarState>(null)
  const [deleteTemplateSnackbar, setDeleteTemplateSnackbar] = useState<SnackbarState>(null)
  const [selectedAvailableFields, setSelectedAvailableFields] = useState<string[]>([])
  const [selectedFields, setSelectedFields] = useState<string[]>([])
  const [selectedSelectedFields, setSelectedSelectedFields] = useState<string[]>([])
  const [lastClickedAvailable, setLastClickedAvailable] = useState<string | null>(null)
  const [lastClickedSelected, setLastClickedSelected] = useState<string | null>(null)
  const [lastClickedLineItemId, setLastClickedLineItemId] = useState<string | null>(null)
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({})
  const [adjustments, setAdjustments] = useState<AdjustmentType[]>([])
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [quoteMenuDialogs, setQuoteMenuDialogs] = useState({
    nteCalculation: false,
    newLine: false,
    newVersion: false,
    clone: false,
    delete: false,
  })
  const leaveBlocker = useBlocker(hasUnsavedLineChanges)
  const [selectedVendorQuoteIds, setSelectedVendorQuoteIds] = useState<string[]>([])

  useBeforeUnload(
    useCallback(
      event => {
        if (hasUnsavedLineChanges) {
          event.preventDefault()
          event.returnValue = ''
        }
      },
      [hasUnsavedLineChanges],
    ),
  )

  const applyView = () => {
    setLoading(true)
    setColumns(
      selectedFields.map(column => ({
        Name: column,
        Width: columnWidths[column] || 1,
        Order: selectedFields.indexOf(column) + 1,
      })),
    )
    setLoading(false)
  }

  const availableFields = Object.keys(COLUMN_MAP).filter(column => !selectedFields.includes(column))

  function selectRange(allKeys: string[], anchor: string, current: string): string[] {
    const anchorIndex = allKeys.indexOf(anchor)
    const currentIndex = allKeys.indexOf(current)
    if (anchorIndex === -1 || currentIndex === -1) return [current]
    const start = Math.min(anchorIndex, currentIndex)
    const end = Math.max(anchorIndex, currentIndex)
    return allKeys.slice(start, end + 1)
  }

  function handleAvailableClick(key: string, shiftKey: boolean) {
    if (shiftKey && lastClickedAvailable && availableFields.includes(lastClickedAvailable)) {
      setSelectedAvailableFields(selectRange(availableFields, lastClickedAvailable, key))
    } else {
      setSelectedAvailableFields(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]))
      setLastClickedAvailable(key)
    }
  }

  function handleSelectedClick(key: string, shiftKey: boolean) {
    if (shiftKey && lastClickedSelected && selectedFields.includes(lastClickedSelected)) {
      setSelectedSelectedFields(selectRange(selectedFields, lastClickedSelected, key))
    } else {
      setSelectedSelectedFields(prev => (prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]))
      setLastClickedSelected(key)
    }
  }

  const isVendorQuoteFiltered = selectedVendorQuoteIds.length > 0 && selectedVendorQuoteIds.length < vendorQuotes.length

  const filteredSourceItems = useMemo(
    () => (isVendorQuoteFiltered ? filterLineItemsByVendorQuote(lineItems, selectedVendorQuoteIds) : null),
    [lineItems, selectedVendorQuoteIds, isVendorQuoteFiltered],
  )

  const displayItems = useMemo(
    () => (filteredSourceItems ? paginateLineItems(filteredSourceItems, page, rowsPerPage) : sortedLineItems),
    [filteredSourceItems, sortedLineItems, page, rowsPerPage],
  )

  const displayLineCount = filteredSourceItems ? countActualLineItems(filteredSourceItems) : lineCount
  const selectAllSource = filteredSourceItems ?? lineItems

  const flatRenderItems = useMemo(() => {
    const items: Array<{ item: quoteLineType; bundleId: string | null; isHeader: boolean }> = []
    displayItems.forEach(item => {
      if (item.LineNumber === -1) {
        items.push({ item, bundleId: item.Bundle || null, isHeader: true })
        item.children?.forEach(lineItem => {
          items.push({ item: lineItem, bundleId: item.Bundle || null, isHeader: false })
        })
      } else {
        items.push({ item, bundleId: null, isHeader: false })
      }
    })
    return items
  }, [displayItems])

  const selectableLineItems = useMemo(
    () => flatRenderItems.filter(renderItem => !renderItem.isHeader).map(renderItem => renderItem.item),
    [flatRenderItems],
  )

  const handleLineItemSelect = useCallback(
    (item: quoteLineType, shiftKey: boolean) => {
      if (shiftKey && lastClickedLineItemId && selectableLineItems.some(i => i.LineItemId === lastClickedLineItemId)) {
        const rangeIds = selectRange(
          selectableLineItems.map(i => i.LineItemId),
          lastClickedLineItemId,
          item.LineItemId,
        )
        setSelectedLineItems(selectableLineItems.filter(i => rangeIds.includes(i.LineItemId)))
      } else {
        setSelectedLineItems(prev => {
          const isSelected = prev.some(selected => selected.LineItemId === item.LineItemId)
          return isSelected ? prev.filter(selected => selected.LineItemId !== item.LineItemId) : [...prev, item]
        })
        setLastClickedLineItemId(item.LineItemId)
      }
    },
    [lastClickedLineItemId, selectableLineItems, setSelectedLineItems],
  )

  const saveTemplate = async () => {
    setLoading(true)
    const templateName = viewName.trim()
    const columns = selectedFields.map(column => ({
      Name: column,
      Width: columnWidths[column] || 1,
      Order: selectedFields.indexOf(column) + 1,
    }))
    axios
      .put(`/customViewTemplate`, {
        userEmail: user.email,
        templateName,
        columns,
      })
      .then(async () => {
        setOpenDialog('')
        setSaveTemplateSnackbar('success')
        await reloadTemplates()
        setSelectedTemplateName(templateName)
      })
      .catch(err => {
        console.error(err)
        setSaveTemplateSnackbar('fail')
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const deleteTemplate = async () => {
    setLoading(true)
    axios
      .delete(`/customViewTemplate/${selectedTemplate.TemplateName}`, {
        data: { userEmail: user.email },
      })
      .then(() => {
        setOpenDialog('')
        setDeleteTemplateSnackbar('success')
        setSelectedTemplateName(null)
        reloadTemplates()
      })
      .catch(err => {
        console.error(err)
        setDeleteTemplateSnackbar('fail')
      })
      .finally(() => {
        setLoading(false)
      })
  }

  useEffect(() => {
    const validColumns = selectedTemplate.Columns.filter(column => Object.hasOwn(COLUMN_MAP, column.Name))
    setColumns(validColumns.map(column => ({ Name: column.Name, Width: column.Width, Order: column.Order })))
    setSelectedFields(validColumns.map(column => column.Name))
    const widths: Record<string, number> = {}
    validColumns.forEach(column => {
      widths[column.Name] = column.Width
    })
    setColumnWidths(widths)
  }, [selectedTemplate])

  useEffect(() => {
    if (openViewDrawer) {
      setSelectedAvailableFields([])
      setSelectedSelectedFields([])
      setLastClickedAvailable(null)
      setLastClickedSelected(null)
    }
  }, [openViewDrawer])

  useEffect(() => {
    setLastClickedLineItemId(null)
  }, [page, rowsPerPage])

  useEffect(() => {
    setSelectedVendorQuoteIds(prev => {
      const pruned = prev.filter(id => vendorQuotes.some(v => v.VendorQuoteId === id))
      return pruned.length === 0 ? vendorQuotes.map(v => v.VendorQuoteId) : pruned
    })
  }, [vendorQuotes])

  // Column widths are percentages of this total, so they always fill the table's container
  // instead of a fixed pixel width - see getColumnWidth in LinesColumns.ts.
  const totalColumnWidth = columns.reduce((sum, column) => sum + column.Width, 0)

  return (
    <div className="lines-view draft-lines">
      <div className="lines-action-bar">
        <ActionBar
          selectedLineItems={selectedLineItems}
          setSelectedLineItems={setSelectedLineItems}
          lineItemCount={displayLineCount}
        />
      </div>
      <Drawer
        className="header-crm-details-drawer"
        open={drawerState.type === 'header-crm-details'}
        anchor="right"
        onClose={() => setDrawerState({ type: null })}
      >
        <HeaderCRMDetails
          setDrawerState={setDrawerState}
          quoteId={quoteId}
          lineItems={lineItems}
          onSaved={handleReloadLineItems}
        />
      </Drawer>
      <div className="lines-header-buttons">
        <div className="lines-header-buttons-left">
          {lineItemsLoading ? (
            <Skeleton width={200} height={40} />
          ) : (
            <TextField
              className="supplier-quote-select"
              label="Supplier Quote"
              value={selectedVendorQuoteIds}
              onChange={e => {
                const value = e.target.value
                const rawValues = typeof value === 'string' ? value.split(',') : value
                if (rawValues[rawValues.length - 1] === 'all') {
                  setSelectedVendorQuoteIds(
                    selectedVendorQuoteIds.length === vendorQuotes.length
                      ? []
                      : vendorQuotes.map(vendorQuote => vendorQuote.VendorQuoteId),
                  )
                } else {
                  setSelectedVendorQuoteIds(rawValues)
                }
                setPage(0)
              }}
              size="small"
              select
              slotProps={{
                inputLabel: { shrink: true },
                select: {
                  multiple: true,
                  displayEmpty: true,
                  renderValue: () =>
                    selectedVendorQuoteIds.length === 0 || selectedVendorQuoteIds.length === vendorQuotes.length
                      ? 'All'
                      : vendorQuotes
                          .filter(vendorQuote => selectedVendorQuoteIds.includes(vendorQuote.VendorQuoteId))
                          .map(vendorQuote =>
                            [vendorQuote.VendorName, vendorQuote.VendorQuoteNumber].filter(Boolean).join(' - '),
                          )
                          .join(', '),
                },
              }}
            >
              <MenuItem value="all">
                <Checkbox
                  checked={vendorQuotes.length > 0 && selectedVendorQuoteIds.length === vendorQuotes.length}
                  size="small"
                />
                All
              </MenuItem>
              {vendorQuotes.map(vendorQuote => (
                <MenuItem key={vendorQuote.VendorQuoteId} value={vendorQuote.VendorQuoteId}>
                  <Checkbox checked={selectedVendorQuoteIds.includes(vendorQuote.VendorQuoteId)} size="small" />
                  {[vendorQuote.VendorName, vendorQuote.VendorQuoteNumber].filter(Boolean).join(' - ')}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            label="Adjustments"
            value={adjustments}
            onChange={e => {
              const value = e.target.value
              setAdjustments(
                typeof value === 'string' ? (value.split(',') as AdjustmentType[]) : (value as AdjustmentType[]),
              )
            }}
            size="small"
            select
            slotProps={{
              select: {
                multiple: true,
                renderValue: selected =>
                  (selected as AdjustmentType[]).length === 0
                    ? 'None'
                    : (selected as AdjustmentType[]).map(value => ADJUSTMENT_LABELS[value]).join(', '),
              },
            }}
          >
            <MenuItem value="add">
              <Checkbox checked={adjustments.includes('add')} size="small" />
              <AddBoxOutlined className="additions-icon" fontSize="small" />
              Additions
            </MenuItem>
            <MenuItem value="differences">
              <Checkbox checked={adjustments.includes('differences')} size="small" />
              <DifferenceOutlined className="differences-icon" fontSize="small" />
              Differences
            </MenuItem>
            <MenuItem value="delete">
              <Checkbox checked={adjustments.includes('delete')} size="small" />
              <DeleteOutlined className="deletions-icon" fontSize="small" />
              Deletions
            </MenuItem>
          </TextField>
          <Divider orientation="vertical" flexItem />
          {templatesLoading || templates.length === 0 ? (
            <Skeleton width={200} height={40} />
          ) : (
            <Autocomplete
              disableClearable
              options={templates.map((template: { TemplateName: string }) => template.TemplateName)}
              value={selectedTemplate.TemplateName}
              onChange={(_, value) => {
                setSelectedTemplateName(value)
              }}
              renderInput={params => <TextField {...params} label="Display Template" />}
              size="small"
            />
          )}
          <IconButton
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              setDrawerState({ type: 'template-menu', anchorEl: e.currentTarget })
            }}
          >
            <MoreVert />
          </IconButton>
          <Menu
            open={drawerState.type === 'template-menu'}
            onClose={() => setDrawerState({ type: null })}
            anchorEl={drawerState.anchorEl}
          >
            <MenuItem
              onClick={() => {
                setOpenViewDrawer(true)
                setDrawerState({ type: null })
              }}
            >
              Edit Display View
            </MenuItem>
            <MenuItem
              onClick={() => {
                setOpenDialog('saveView')
                setDrawerState({ type: null })
                setViewName(selectedTemplate.TemplateName !== 'Default' ? selectedTemplate.TemplateName : '')
              }}
            >
              Save View
            </MenuItem>
            <MenuItem
              onClick={() => {
                setOpenDialog('deleteView')
                setDrawerState({ type: null })
              }}
            >
              Delete Template
            </MenuItem>
          </Menu>
          <Divider orientation="vertical" flexItem />
          <Button
            variant="contained"
            onClick={async () => {
              try {
                await handleEditModeToggle()
              } catch (err) {
                setBulkEditSnackbar('fail')
                console.error('Error in handleEditModeToggle: ', err)
              }
            }}
          >
            {!canEdit ? 'Edit Lines' : 'Save Line Changes'}
          </Button>
          {canEdit && (
            <Button
              variant="outlined"
              disabled={!hasUnsavedLineChanges}
              onClick={() => {
                handleResetLines()
              }}
            >
              Reset Lines
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={() => {
              setDrawerState({ type: 'header-crm-details' })
            }}
          >
            Header & CRM Details
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setOpenDialog('import')
            }}
          >
            Supplier Quotes
          </Button>
          <Button
            variant="outlined"
            startIcon={<MoreVert />}
            endIcon={<ArrowDropDown />}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              setDrawerState({ type: 'quote-menu', anchorEl: e.currentTarget })
            }}
          />
          <QuoteMenu
            open={drawerState.type === 'quote-menu'}
            onClose={() => setDrawerState({ type: null })}
            anchorEl={drawerState.anchorEl ?? null}
            onNewLine={() => {
              setDrawerState({ type: null })
              setQuoteMenuDialogs(prev => ({ ...prev, newLine: true }))
            }}
            onNewVersion={() => {
              setDrawerState({ type: null })
              setQuoteMenuDialogs(prev => ({ ...prev, newVersion: true }))
            }}
            onClone={() => {
              setDrawerState({ type: null })
              setQuoteMenuDialogs(prev => ({ ...prev, clone: true }))
            }}
            onDelete={() => {
              setDrawerState({ type: null })
              setQuoteMenuDialogs(prev => ({ ...prev, delete: true }))
            }}
            onNTECalculation={() => {
              setDrawerState({ type: null })
              setQuoteMenuDialogs(prev => ({ ...prev, nteCalculation: true }))
            }}
            onITESTools={() => {
              setDrawerState({ type: null })
            }}
            onRFQ={() => {
              setDrawerState({ type: null })
            }}
          />
          <NewVersionDialog
            open={quoteMenuDialogs.newVersion}
            onClose={() => setQuoteMenuDialogs(prev => ({ ...prev, newVersion: false }))}
            quoteId={quoteId ?? ''}
            nextVersion={versions.length + 1}
          />
          <CloneDialog
            open={quoteMenuDialogs.clone}
            onClose={() => setQuoteMenuDialogs(prev => ({ ...prev, clone: false }))}
            quoteId={quoteId ?? ''}
          />
          <DeleteQuoteDialog
            open={quoteMenuDialogs.delete}
            onClose={() => setQuoteMenuDialogs(prev => ({ ...prev, delete: false }))}
            quoteId={quoteId ?? ''}
          />
          <NTECalculationDialog
            open={quoteMenuDialogs.nteCalculation}
            onClose={() => setQuoteMenuDialogs(prev => ({ ...prev, nteCalculation: false }))}
            quoteId={quoteId ?? ''}
          />
          <Drawer
            open={quoteMenuDialogs.newLine}
            onClose={() => setQuoteMenuDialogs(prev => ({ ...prev, newLine: false }))}
            anchor="right"
          >
            <LineEdit
              mode="create"
              lineNumber={0}
              onClose={() => setQuoteMenuDialogs(prev => ({ ...prev, newLine: false }))}
              quoteId={quoteId ?? ''}
              bundle={drawerState.bundle ?? null}
            />
          </Drawer>
        </div>
        <div className="lines-header-buttons-right">
          <TablePagination
            count={displayLineCount}
            onPageChange={(_, newPage) => {
              setPage(newPage)
            }}
            page={page}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[25, 50, 75, 100]}
            onRowsPerPageChange={e => {
              setRowsPerPage(parseInt(e.target.value))
              setPage(0)
            }}
            component="div"
          />
        </div>
      </div>
      <DragDropContext
        onDragEnd={result => {
          handleDrag(result)
        }}
      >
        <TableContainer>
          <div className="draft-lines-scroll-content">
            <Table stickyHeader sx={{ width: `${totalColumnWidth * 100}px` }}>
              <TableHead className="lines-table-header">
                <TableRow>
                  {columns.map(column => {
                    const columnConfig = COLUMN_MAP[column.Name]
                    return (
                      <TableCell
                        key={column.Name}
                        className={column.Name === '#' ? 'lines-sticky-col' : undefined}
                        sx={{
                          width: getColumnWidth(column.Width, totalColumnWidth),
                          minWidth: getColumnWidth(column.Width, totalColumnWidth),
                        }}
                        align={
                          columnConfig.format === 'currency' ||
                          columnConfig.format === 'percentage' ||
                          (columnConfig.format === 'number' && column.Name !== '#')
                            ? 'right'
                            : 'left'
                        }
                      >
                        {column.Name === '#' ? (
                          <Checkbox
                            checked={displayLineCount > 0 && selectedLineItems.length === displayLineCount}
                            indeterminate={selectedLineItems.length > 0 && selectedLineItems.length < displayLineCount}
                            onChange={e => {
                              setSelectedLineItems(
                                e.target.checked
                                  ? [
                                      ...selectAllSource.flatMap(item => item.children ?? []),
                                      ...selectAllSource.filter(item => item.LineNumber !== -1),
                                    ]
                                  : [],
                              )
                            }}
                          />
                        ) : null}
                        {canEdit && !COLUMN_MAP[column.Name].nonEdit && (
                          <EditOutlined fontSize="inherit" className="lines-editable-header-icon" />
                        )}
                        {COLUMN_MAP[column.Name].label ?? column.Name}
                      </TableCell>
                    )
                  })}
                  <TableCell key="Actions" className="lines-actions-col"></TableCell>
                </TableRow>
              </TableHead>
              {lineItemsLoading ? (
                <TableBody>
                  {Array.from({ length: 5 }).map((_, index) => (
                    <TableRow key={index}>
                      {columns.map(column => {
                        const columnConfig = COLUMN_MAP[column.Name]
                        return (
                          <TableCell
                            key={column.Name}
                            className={column.Name === '#' ? 'lines-sticky-col' : undefined}
                            align={
                              columnConfig.format === 'currency' ||
                              columnConfig.format === 'percentage' ||
                              (columnConfig.format === 'number' && column.Name !== '#')
                                ? 'right'
                                : 'left'
                            }
                            sx={{
                              width: getColumnWidth(column.Width, totalColumnWidth),
                              minWidth: getColumnWidth(column.Width, totalColumnWidth),
                            }}
                          >
                            <Skeleton variant="text" />
                          </TableCell>
                        )
                      })}
                      <TableCell key="Actions" className="lines-actions-col">
                        <Skeleton width={25} height={35} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              ) : (
                <Droppable droppableId="all-items">
                  {provided => {
                    return (
                      <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                        {flatRenderItems.map((renderItem, index) => {
                          let isLastChildInBundle = false
                          if (!renderItem.isHeader && renderItem.bundleId !== null) {
                            const fullBundle = displayItems.find(
                              item => item.Bundle === renderItem.bundleId && item.LineNumber === -1,
                            )
                            if (fullBundle?.children && fullBundle.children.length > 0) {
                              const lastChildInBundle = fullBundle.children[fullBundle.children.length - 1]
                              isLastChildInBundle = lastChildInBundle.LineItemId === renderItem.item.LineItemId
                            }
                          }

                          const bundleHeader = renderItem.isHeader
                            ? displayItems.find(item => item.LineItemId === renderItem.item.LineItemId)
                            : null
                          return (
                            <Draggable
                              key={renderItem.item.LineItemId}
                              draggableId={renderItem.item.LineItemId}
                              index={index}
                              isDragDisabled={!canEdit || isVendorQuoteFiltered}
                            >
                              {provided => (
                                <TableRow
                                  className={`${
                                    bundleHeader
                                      ? 'bundle-row'
                                      : renderItem.bundleId === null
                                      ? 'no-bundle'
                                      : isLastChildInBundle
                                      ? 'child-row-last'
                                      : ''
                                  } ${
                                    differences &&
                                    !bundleHeader &&
                                    differences.additions.some(
                                      addition => addition.LineItemId === renderItem.item.LineItemId,
                                    ) &&
                                    adjustments.includes('add')
                                      ? 'additions'
                                      : ''
                                  } ${
                                    differences &&
                                    !bundleHeader &&
                                    differences.deletions.some(
                                      deletion => deletion.LineItemId === renderItem.item.LineItemId,
                                    ) &&
                                    adjustments.includes('delete')
                                      ? 'deletions'
                                      : ''
                                  }`}
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  {...provided.dragHandleProps}
                                  sx={{
                                    visibility:
                                      collapsedBundles.has(renderItem.bundleId ?? 'no-bundle') && !bundleHeader
                                        ? 'hidden'
                                        : 'visible',
                                    display:
                                      collapsedBundles.has(renderItem.bundleId ?? 'no-bundle') && !bundleHeader
                                        ? 'none'
                                        : 'table-row',
                                  }}
                                  onContextMenu={(e: React.MouseEvent) => {
                                    e.preventDefault()
                                    setDrawerState({
                                      type: 'menu',
                                      lineItem: renderItem.item,
                                      anchorEl: null,
                                      anchorPosition: { top: e.clientY, left: e.clientX },
                                    })
                                  }}
                                >
                                  {columns.map(column => {
                                    const columnConfig = COLUMN_MAP[column.Name]
                                    const cellCanEdit =
                                      canEdit &&
                                      !columnConfig.nonEdit &&
                                      (!bundleHeader ||
                                        ('property' in columnConfig
                                          ? !BUNDLE_NON_EDIT_PROPERTIES.includes(columnConfig.property)
                                          : false))
                                    return column.Name !== '#' ? (
                                      <TableCell
                                        className={[
                                          differences &&
                                          adjustments.includes('differences') &&
                                          differences.changes.some(change => {
                                            const changeWithDifferences = change as quoteLineType & {
                                              //eslint-disable-next-line @typescript-eslint/no-explicit-any
                                              columnDifferences?: Record<string, { previous: any; current: any }>
                                            }
                                            const columnConfig = COLUMN_MAP[column.Name]
                                            const property = 'property' in columnConfig ? columnConfig.property : null
                                            return (
                                              (changeWithDifferences.LineItemId === renderItem.item.LineItemId ||
                                                changeWithDifferences.MfrPartNumber ===
                                                  renderItem.item.MfrPartNumber) &&
                                              property != null &&
                                              changeWithDifferences.columnDifferences?.[property]
                                            )
                                          })
                                            ? 'differences'
                                            : null,
                                          column.Name === 'Description' ? 'lines-ellipsis-col' : null,
                                          [
                                            'Mfr Part Number',
                                            'Qty',
                                            'Ext Cost',
                                            'Unit Cost',
                                            'Unit Price',
                                            'Ext Price',
                                            'Contract Ceiling Price',
                                            'Gross Profit (%)',
                                            'Gross Profit ($)',
                                          ].includes(column.Name)
                                            ? 'bundle-small-text'
                                            : null,
                                          cellCanEdit ? 'lines-editable-col' : null,
                                        ]
                                          .filter(Boolean)
                                          .join(' ')}
                                        key={column.Name}
                                        align={
                                          COLUMN_MAP[column.Name].format === 'currency' ||
                                          COLUMN_MAP[column.Name].format === 'percentage' ||
                                          (COLUMN_MAP[column.Name].format === 'number' && column.Name !== '#')
                                            ? 'right'
                                            : 'left'
                                        }
                                        sx={{
                                          width: getColumnWidth(column.Width, totalColumnWidth),
                                          minWidth: getColumnWidth(column.Width, totalColumnWidth),
                                        }}
                                      >
                                        <EditableCell
                                          value={getColumnValue(renderItem.item, column.Name, true)}
                                          contractNumber={quote.ContractNumber}
                                          canEdit={cellCanEdit}
                                          inputType={columnConfig.format}
                                          source={columnConfig.masterList}
                                          options={
                                            'property' in columnConfig && columnConfig.property === 'ProductCategory'
                                              ? productCategoryOptions
                                              : columnConfig.options
                                          }
                                          displayValue={
                                            'property' in columnConfig &&
                                            columnConfig.property === 'ProductCategory' &&
                                            (renderItem.item.ProductCategory === null ||
                                              renderItem.item.ProductCategory === undefined) &&
                                            renderItem.item.ProductCategorySource === 'PENDING_AI' ? (
                                              <AiClassifyingLabel />
                                            ) : undefined
                                          }
                                          onChange={value =>
                                            setLineItems(prev => {
                                              if ('property' in columnConfig) {
                                                if (column.Name === 'Manufacturer') {
                                                  return updateLineItemProperty(
                                                    updateLineItemProperty(
                                                      prev,
                                                      renderItem.item.LineItemId,
                                                      'Manufacturer',
                                                      value.Manufacturer,
                                                    ),
                                                    renderItem.item.LineItemId,
                                                    'OEMCode',
                                                    value.OEMCode,
                                                  )
                                                }
                                                return updateLineItemProperty(
                                                  prev,
                                                  renderItem.item.LineItemId,
                                                  columnConfig.property,
                                                  value,
                                                )
                                              } else {
                                                return prev
                                              }
                                            })
                                          }
                                        />
                                        {differences &&
                                        adjustments.includes('differences') &&
                                        differences.changes.some(change => {
                                          const changeWithDifferences = change as quoteLineType & {
                                            //eslint-disable-next-line @typescript-eslint/no-explicit-any
                                            columnDifferences?: Record<string, { previous: any; current: any }>
                                          }
                                          const columnConfig = COLUMN_MAP[column.Name]
                                          const property = 'property' in columnConfig ? columnConfig.property : null
                                          return (
                                            (changeWithDifferences.LineItemId === renderItem.item.LineItemId ||
                                              changeWithDifferences.MfrPartNumber === renderItem.item.MfrPartNumber) &&
                                            property != null &&
                                            changeWithDifferences.columnDifferences?.[property]
                                          )
                                        }) ? (
                                          <>
                                            <br />
                                            <DifferenceOutlined className="differences-icon" />
                                          </>
                                        ) : null}
                                      </TableCell>
                                    ) : (
                                      <TableCell
                                        key={column.Name}
                                        className="lines-sticky-col"
                                        align={'left'}
                                        sx={{
                                          width: getColumnWidth(column.Width, totalColumnWidth),
                                        }}
                                      >
                                        <Checkbox
                                          checked={
                                            bundleHeader
                                              ? selectedLineItems.filter(
                                                  lineItem => lineItem.Bundle === bundleHeader.Bundle,
                                                ).length === (bundleHeader.children?.length ?? 0)
                                              : selectedLineItems.some(
                                                  selected => selected.LineItemId === renderItem.item.LineItemId,
                                                )
                                          }
                                          indeterminate={
                                            !!bundleHeader &&
                                            selectedLineItems.filter(
                                              lineItem => lineItem.Bundle === bundleHeader.Bundle,
                                            ).length > 0 &&
                                            selectedLineItems.filter(
                                              lineItem => lineItem.Bundle === bundleHeader.Bundle,
                                            ).length < (bundleHeader.children?.length ?? 0)
                                          }
                                          onClick={e => {
                                            if (bundleHeader) {
                                              if (
                                                selectedLineItems.filter(
                                                  lineItem => lineItem.Bundle === bundleHeader.Bundle,
                                                ).length > 0
                                              ) {
                                                setSelectedLineItems(prev =>
                                                  prev.filter(lineItem => lineItem.Bundle !== bundleHeader.Bundle),
                                                )
                                              } else {
                                                setSelectedLineItems(prev => [
                                                  ...prev,
                                                  ...(bundleHeader.children ?? []),
                                                ])
                                              }
                                            } else {
                                              handleLineItemSelect(renderItem.item, e.shiftKey)
                                            }
                                          }}
                                        />
                                        {!bundleHeader && ' '}
                                        {bundleHeader ? '' : renderItem.item.LineNumber}
                                        {differences &&
                                        differences.additions.some(
                                          addition => addition.LineItemId === renderItem.item.LineItemId,
                                        ) &&
                                        adjustments.includes('add') ? (
                                          <>
                                            <br />
                                            <AddBoxOutlined className="additions-icon" />
                                          </>
                                        ) : differences &&
                                          differences.deletions.some(
                                            deletion => deletion.LineItemId === renderItem.item.LineItemId,
                                          ) &&
                                          adjustments.includes('delete') ? (
                                          <>
                                            <br />
                                            <DeleteOutlined className="deletions-icon" />
                                          </>
                                        ) : null}
                                        {bundleHeader && (
                                          <IconButton
                                            onClick={() =>
                                              setCollapsedBundles(prev => {
                                                const newSet = new Set(prev)
                                                newSet.has(renderItem.item.Bundle ?? 'no-bundle')
                                                  ? newSet.delete(renderItem.item.Bundle ?? 'no-bundle')
                                                  : newSet.add(renderItem.item.Bundle ?? 'no-bundle')
                                                return newSet
                                              })
                                            }
                                          >
                                            {collapsedBundles.has(renderItem.item.Bundle ?? 'no-bundle') ? (
                                              <ExpandLess />
                                            ) : (
                                              <ExpandMore />
                                            )}
                                          </IconButton>
                                        )}
                                      </TableCell>
                                    )
                                  })}
                                  <TableCell key="Actions" className="lines-actions-col">
                                    <IconButton
                                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                        setDrawerState({
                                          type: 'menu',
                                          lineItem: renderItem.item,
                                          anchorEl: e.currentTarget,
                                        })
                                      }}
                                    >
                                      <MoreVert />
                                    </IconButton>
                                  </TableCell>
                                </TableRow>
                              )}
                            </Draggable>
                          )
                        })}
                        {provided.placeholder}
                      </TableBody>
                    )
                  }}
                </Droppable>
              )}
            </Table>
            <QuoteSummary />
          </div>
          <div className="lines-fab">
            <Fab
              variant="extended"
              size="large"
              color={validateLoading ? 'default' : 'primary'}
              disabled={validateLoading || loading || canEdit}
              onClick={() => {
                setValidateLoading(true)
                axios
                  .get(`/quote/${quoteId}/validate`)
                  .then(res => {
                    setLineItems(res.data)
                    setView(quoteStatuses.indexOf('Validate'))
                  })
                  .catch(err => {
                    console.error(err)
                  })
                  .finally(() => {
                    setValidateLoading(false)
                  })
              }}
            >
              <span style={{ visibility: validateLoading ? 'hidden' : 'visible' }}>Validate</span>
              {validateLoading && <CircularProgress size={20} color="inherit" />}
            </Fab>
          </div>
        </TableContainer>
      </DragDropContext>

      <Drawer
        className="supplier-quotes-drawer"
        open={openDialog === 'import'}
        onClose={() => setOpenDialog('')}
        anchor="right"
      >
        <SupplierQuotesSidebar
          onClose={() => setOpenDialog('')}
          quoteId={quoteId ?? ''}
          vendorQuotes={vendorQuotes}
          lineItems={lineItems}
          onDeleteVendorQuote={handleDeleteVendorQuote}
        />
      </Drawer>

      <Dialog open={openDialog === 'deleteView'} onClose={() => setOpenDialog('')} fullWidth>
        <DialogTitle variant="h6">Delete Template</DialogTitle>
        <DialogContent className="lines-dialog-content">
          <Typography>Are you sure you want to delete this template? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setOpenDialog('')}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => deleteTemplate()} loading={loading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDialog === 'saveView'} onClose={() => setOpenDialog('')} fullWidth>
        <DialogTitle variant="h6">Save View</DialogTitle>
        <DialogContent className="lines-dialog-content">
          <Typography>Enter a view name:</Typography>
          <TextField fullWidth label="Name" size="small" value={viewName} onChange={e => setViewName(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setOpenDialog('')}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => saveTemplate()} loading={loading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Drawer open={openViewDrawer} onClose={() => setOpenViewDrawer(false)} anchor="right" className="lines-drawer">
        <div className="lines-drawer-header">
          <Typography variant="h6">Edit Display Fields</Typography>
          <div className="lines-drawer-header-buttons">
            <Button
              variant="outlined"
              onClick={() => {
                const validColumns = selectedTemplate.Columns.filter(column => Object.hasOwn(COLUMN_MAP, column.Name))
                setSelectedFields(validColumns.map(column => column.Name))
                const widths: Record<string, number> = {}
                validColumns.forEach(column => {
                  widths[column.Name] = column.Width
                })
                setColumnWidths(widths)
              }}
            >
              Reset
            </Button>
            <Button variant="contained" onClick={() => applyView()}>
              Apply
            </Button>
          </div>
        </div>
        <div className="lines-drawer-content">
          <Table className="lines-drawer-table" color="primary">
            <TableHead>
              <TableRow>
                <TableCell>Available Fields</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {availableFields.map(column => (
                <TableRow key={column}>
                  <TableCell>
                    <Checkbox
                      size="small"
                      checked={selectedAvailableFields.includes(column)}
                      onClick={e => handleAvailableClick(column, e.shiftKey)}
                    />{' '}
                    {COLUMN_MAP[column].label ?? column}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="lines-drawer-buttons">
            <IconButton
              onClick={() => {
                const fieldsToAdd = Object.keys(COLUMN_MAP).filter(column => selectedAvailableFields.includes(column))
                setSelectedFields(prev => [...prev, ...fieldsToAdd])
                setColumnWidths(prev => {
                  const newWidths = { ...prev }
                  fieldsToAdd.forEach(field => {
                    if (!newWidths[field]) {
                      newWidths[field] = 1
                    }
                  })
                  return newWidths
                })
                setSelectedAvailableFields([])
              }}
            >
              <ChevronRight />
            </IconButton>
            <IconButton
              onClick={() => {
                setSelectedFields(prev => prev.filter(column => !selectedSelectedFields.includes(column)))
                setColumnWidths(prev => {
                  const newWidths = { ...prev }
                  selectedSelectedFields.forEach(field => {
                    delete newWidths[field]
                  })
                  return newWidths
                })
                setSelectedSelectedFields([])
              }}
            >
              <ChevronLeft />
            </IconButton>
          </div>
          <DragDropContext
            onDragEnd={result => {
              if (!result.destination || result.destination.index === result.source.index) {
                return
              }

              const newFields = [...selectedFields]
              const [movedField] = newFields.splice(result.source.index, 1)
              newFields.splice(result.destination.index, 0, movedField)

              setSelectedFields(newFields)
              setColumnWidths(prev => {
                const newWidths: Record<string, number> = {}
                newFields.forEach(field => {
                  newWidths[field] = prev[field] || 1
                })
                return newWidths
              })
            }}
          >
            <Table className="lines-drawer-table display-fields-table" color="primary">
              <TableHead>
                <TableRow>
                  <TableCell>Selected Fields</TableCell>
                  <TableCell>Width</TableCell>
                </TableRow>
              </TableHead>
              <Droppable droppableId="selectedFields">
                {provided => (
                  <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                    {selectedFields.map((column, index) => (
                      <Draggable key={column} draggableId={column} index={index}>
                        {provided => (
                          <TableRow
                            className="draggable-row"
                            key={column}
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={{ ...provided.draggableProps.style }}
                          >
                            <TableCell className="label-cell">
                              <DragIndicator className="drag-indicator" fontSize="small" />
                              <Checkbox
                                size="small"
                                checked={selectedSelectedFields.includes(column)}
                                onClick={e => {
                                  e.stopPropagation()
                                  handleSelectedClick(column, e.shiftKey)
                                }}
                              />
                              {COLUMN_MAP[column].label ?? column}
                            </TableCell>
                            <TableCell>
                              <Input
                                className="display-fields-number-input"
                                type="number"
                                value={columnWidths[column] || 1}
                                onChange={e => {
                                  const width = Math.max(1, Math.min(10, Number(e.target.value)))
                                  setColumnWidths(prev => ({ ...prev, [column]: width }))
                                }}
                                inputProps={{ min: 0.1, max: 10, step: 0.1 }}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </TableBody>
                )}
              </Droppable>
            </Table>
          </DragDropContext>
        </div>
      </Drawer>

      <Menu
        open={drawerState.type === 'menu'}
        onClose={() => setDrawerState({ type: null })}
        anchorReference={drawerState.anchorPosition ? 'anchorPosition' : 'anchorEl'}
        anchorEl={drawerState.anchorEl}
        anchorPosition={drawerState.anchorPosition ?? undefined}
        disableScrollLock={true}
      >
        <MenuItem onClick={() => handleMenuAction('new')}>New Line</MenuItem>
        <MenuItem onClick={() => handleMenuAction('clone')}>Clone</MenuItem>
        {drawerState.lineItem?.Bundle && <MenuItem onClick={() => handleMenuAction('unbundle')}>Unbundle</MenuItem>}
        {drawerState.lineItem?.LineNumber !== -1 && (
          <MenuItem onClick={() => setDeleteDialogOpen(true)}>Delete</MenuItem>
        )}
      </Menu>
      <DeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={() => {
          handleDelete(drawerState.lineItem ?? null)
          setDrawerState({ type: null })
        }}
      />
      <Dialog open={leaveBlocker.state === 'blocked'} onClose={() => leaveBlocker.reset?.()}>
        <DialogTitle variant="h6">Unsaved Changes</DialogTitle>
        <DialogContent>
          <Typography>You have unsaved line changes. Leave this page without saving?</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => leaveBlocker.reset?.()}>
            Stay
          </Button>
          <Button variant="contained" onClick={() => leaveBlocker.proceed?.()}>
            Leave
          </Button>
        </DialogActions>
      </Dialog>
      <Drawer open={drawerState.type === 'edit'} onClose={() => setDrawerState({ type: null })} anchor="right">
        <LineEdit
          onClose={() => setDrawerState({ type: null })}
          selectedLineItems={drawerState.lineItem ? [drawerState.lineItem] : []}
          quoteId={quoteId ?? ''}
        />
      </Drawer>
      <Drawer
        open={drawerState.type === 'new'}
        onClose={() => {
          setDrawerState({ type: null })
        }}
        anchor="right"
      >
        <LineEdit
          mode="create"
          lineNumber={drawerState.lineNumber ?? 0}
          onClose={() => {
            setDrawerState({ type: null })
            setAddLineSnackbar('success')
          }}
          quoteId={quoteId ?? ''}
          bundle={drawerState.bundle ?? null}
        />
      </Drawer>

      <Alert
        severity="info"
        className="lines-edit-alert"
        sx={{
          bottom:
            cloneSnackbar || deleteSnackbar || bulkEditSnackbar || bulkDeleteSnackbar || addLineSnackbar
              ? '90px'
              : '24px',
          opacity: canEdit ? 1 : 0,
          visibility:
            canEdit && !(cloneSnackbar || deleteSnackbar || bulkEditSnackbar || bulkDeleteSnackbar || addLineSnackbar)
              ? 'visible'
              : 'hidden',
        }}
      >
        <AlertTitle className="lines-edit-alert-title">
          <div>Edit Lines Enabled</div>
          <Button variant="text" onClick={() => handleEditModeToggle()}>
            Save line changes
          </Button>
        </AlertTitle>
        <Typography variant="body2">You can now edit the lines. Save to confirm your changes.</Typography>
      </Alert>
      <Snackbar
        autoHideDuration={5000}
        open={cloneSnackbar === 'success'}
        onClose={() => setCloneSnackbar(null)}
        message="Line cloned successfully"
      />
      <Snackbar open={cloneSnackbar === 'fail'}>
        <Alert severity="error" variant="filled" onClose={() => setCloneSnackbar(null)}>
          Failed to clone line item. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
      <Snackbar
        open={deleteSnackbar === 'success'}
        onClose={() => setDeleteSnackbar(null)}
        message="Line item deleted successfully"
      />
      <Snackbar open={deleteSnackbar === 'fail'}>
        <Alert severity="error" variant="filled" onClose={() => setDeleteSnackbar(null)}>
          Failed to delete line item. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
      <Snackbar
        autoHideDuration={5000}
        open={bulkEditSnackbar === 'success'}
        onClose={() => setBulkEditSnackbar(null)}
        message="Line items updated successfully"
      />
      <Snackbar open={bulkEditSnackbar === 'fail'}>
        <Alert severity="error" variant="filled" onClose={() => setBulkEditSnackbar(null)}>
          Failed to edit line items. Please try again or contact support if the problem persists.
          <Button
            variant="text"
            color="inherit"
            size="small"
            onClick={async () => {
              try {
                setBulkEditSnackbar(null)
                await handleEditModeToggle()
              } catch (err) {
                setBulkEditSnackbar('fail')
                console.error('Error in handleEditModeToggle: ', err)
              }
            }}
          >
            Retry
          </Button>
        </Alert>
      </Snackbar>
      <Snackbar
        autoHideDuration={5000}
        open={addLineSnackbar === 'success'}
        onClose={() => setAddLineSnackbar(null)}
        message="Line added successfully"
      />
      <Snackbar
        autoHideDuration={5000}
        open={bundleSnackbar === 'success'}
        onClose={() => setBundleSnackbar(null)}
        message="Line items bundled successfully"
      />
      <Snackbar
        autoHideDuration={5000}
        open={unbundleSnackbar === 'success'}
        onClose={() => setUnbundleSnackbar(null)}
        message="Line items unbundled successfully"
      />
      <Snackbar open={unbundleSnackbar === 'fail'}>
        <Alert severity="error" variant="filled" onClose={() => setUnbundleSnackbar(null)}>
          Failed to unbundle line items. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
      <Snackbar
        autoHideDuration={5000}
        open={bulkDeleteSnackbar === 'success'}
        onClose={() => setBulkDeleteSnackbar(null)}
        message="Bundle and line items deleted successfully"
      />
      <Snackbar open={bulkDeleteSnackbar === 'fail'}>
        <Alert severity="error" variant="filled" onClose={() => setBulkDeleteSnackbar(null)}>
          Failed to delete bundle and line items. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
      <Snackbar open={bundleSnackbar === 'fail'}>
        <Alert severity="error" variant="filled" onClose={() => setBundleSnackbar(null)}>
          Failed to bundle line items. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
      <Snackbar
        autoHideDuration={5000}
        open={saveTemplateSnackbar === 'success'}
        onClose={() => setSaveTemplateSnackbar(null)}
        message="Template saved successfully"
      />
      <Snackbar open={saveTemplateSnackbar === 'fail'}>
        <Alert severity="error" variant="filled" onClose={() => setSaveTemplateSnackbar(null)}>
          Failed to save template. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
      <Snackbar
        autoHideDuration={5000}
        open={deleteTemplateSnackbar === 'success'}
        onClose={() => setDeleteTemplateSnackbar(null)}
        message="Template deleted successfully"
      />
      <Snackbar open={deleteTemplateSnackbar === 'fail'}>
        <Alert severity="error" variant="filled" onClose={() => setDeleteTemplateSnackbar(null)}>
          Failed to delete template. Please try again or contact support if the problem persists.
        </Alert>
      </Snackbar>
    </div>
  )
}

export default DraftLines
